// Poza maskotki po każdej próbie „powiedz zdanie” (7 póz z makiet; numeracja 1a–7a z arkusza użytkownika, patch-42). Dobór wg wyniku:
//   > 90 %   r-trophy (4a)    > 80 %  r-cheer (3a)    ≥ 70 % (zaliczone) r-coffee (1a) albo r-lounge (2a) na zmianę — oba „spokojnie, dobrze”
//   < 60 %   r-shrug (5a)     < 50 %  r-think (6a)    ostatnia próba bez zaliczenia (3. i dalsze) r-sleep (7a) — „dość na dziś, idziemy dalej”
// Progi są w `config.reaction`; 70 % to próg zaliczenia (`config.scoring.minAccuracy`). Bez procentu (samoocena, przeglądarka bez oceny głosek) wybieramy
// po werdykcie: zaliczone → kawa/leżak (3★ → radość), słabe słowo albo niepewne rozpoznanie → „hmm”, reszta → wzruszenie ramion.
import { config as defaultConfig } from '../config.ts';
import type { Pose } from '../ui/kit.ts';
import type { Verdict } from '../types.ts';

export interface ReactionInput {
  verdict: Pick<Verdict, 'passed' | 'stars' | 'reason'>;
  /** Procent z oceny Azure (brak = samoocena / przeglądarka). */
  percent?: number;
  /** To była ostatnia dozwolona próba i bez zaliczenia (zaraz idziemy dalej). */
  exhausted?: boolean;
  /** Samoocena dziecka (przyciski „Dobrze mi poszło” / „Jeszcze raz”). */
  self?: boolean;
  /** Licznik do przeplatania kawy i leżaka przy spokojnym „dobrze”: parzysty → kawa, nieparzysty → leżak. */
  alt?: number;
}

export type ReactionPose = Extract<Pose, 'r-trophy' | 'r-cheer' | 'r-coffee' | 'r-lounge' | 'r-think' | 'r-shrug' | 'r-sleep'>;

const relaxed = (alt = 0): ReactionPose => (alt % 2 === 0 ? 'r-coffee' : 'r-lounge');

/** Progi z `config.reaction` (liczby, nie literały — testy podają własne). */
export interface ReactionConfig {
  reaction: { readonly trophy: number; readonly cheer: number; readonly shrug: number; readonly think: number };
}

export function reactionPose(i: ReactionInput, cfg: ReactionConfig = defaultConfig): ReactionPose {
  const { verdict } = i;
  const p = i.percent;
  if (verdict.passed) {
    if (i.self) return relaxed(i.alt);
    if (p === undefined) return verdict.stars >= 3 ? 'r-cheer' : relaxed(i.alt);
    if (p > cfg.reaction.trophy) return 'r-trophy';
    if (p > cfg.reaction.cheer) return 'r-cheer';
    return relaxed(i.alt);
  }
  if (i.exhausted) return 'r-sleep';
  const unsure = verdict.reason === 'weak-words' || verdict.reason === 'unclear';
  if (i.self) return 'r-think';
  if (p === undefined) return unsure ? 'r-think' : 'r-shrug';
  if (p < cfg.reaction.think) return 'r-think';
  if (p < cfg.reaction.shrug) return 'r-shrug';
  return unsure ? 'r-think' : 'r-shrug'; // 60–70 %: prawie, ale nie zaliczone — „hmm, które słowo?” albo wzruszenie ramion
}
